import axios, { AxiosError, InternalAxiosRequestConfig, AxiosHeaders } from 'axios';
import { getResourceTenantId, RESOURCE_TENANT_HEADER } from './resourceTenantProvider';
import { getApiGatewayUrl } from './apiGatewayUrl';
import { refreshSession } from './sessionRefresh';
import { notifyHttpRejection } from './httpRejectionEvent';

/**
 * Shared Axios instance with interceptors for authentication and error handling
 *
 * Features:
 * - Automatic Authorization header (JWT from HttpOnly cookie via withCredentials)
 * - Correlation ID generation for request tracing
 * - Reactive 401 handling: transparently refresh the access cookie and replay
 *   the request once before ever logging out (see response interceptor)
 * - Error logging with correlation IDs
 */

// Gateway origin comes from the canonical resolver (runtime env-config in
// deployed environments, secure local endpoint otherwise) — see apiGatewayUrl.ts.
const API_GATEWAY_URL = getApiGatewayUrl();

const CSRF_COOKIE_NAME = 'xsrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

const axiosInstance = axios.create({
  baseURL: API_GATEWAY_URL,
  withCredentials: true, // Required for HttpOnly cookie transmission
  timeout: 30000, // 30-second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

function getCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  const [, value] = cookie.split('=');
  return decodeURIComponent(value);
}

/**
 * Request interceptor: Add correlation ID for distributed tracing
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Generate correlation ID for request tracing using Web Crypto API
    const correlationId = crypto.randomUUID();
    const headers = AxiosHeaders.from(config.headers ?? {});
    headers.set('X-Correlation-Id', correlationId);

    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      headers.set(CSRF_HEADER_NAME, csrfToken);
    }

    // Apply the ambient (ClientSwitcher) tenant only when the caller did not set the header
    // explicitly. A per-request X-Resource-Tenant-Id must win — e.g. the invite flow scopes the
    // request to a specific selected client, which may differ from the ambient tenant. This
    // matches the Orval custom-fetch mutator, where per-call headers also take precedence.
    const resourceTenantId = getResourceTenantId();
    if (resourceTenantId && !headers.has(RESOURCE_TENANT_HEADER)) {
      headers.set(RESOURCE_TENANT_HEADER, resourceTenantId);
    }

    return {
      ...config,
      headers,
    };
  },
  (error: AxiosError) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Endpoints that must NOT trigger the reactive refresh-and-retry. `/refresh`
 * is the refresh call itself (retrying it would recurse); `/logout` is a
 * teardown that a 401 should simply let through.
 */
function isAuthLifecycleEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return url.includes('/api/auth/refresh') || url.includes('/api/auth/logout');
}

/**
 * Response interceptor: reactive token refresh, then 401 → logout fallback.
 *
 * A 401 almost always means the short-lived access cookie lapsed in the gap
 * between proactive refreshes (throttled background tab, a burst of concurrent
 * requests, clock skew, device sleep). Instead of logging the user out on the
 * first 401 — which boots an actively-working user mid-task — we:
 *   1. call `refreshSession()` (single-flight, shared with the proactive timer
 *      and the orval client, so a burst of 401s triggers exactly one refresh);
 *   2. on success, replay the original request transparently;
 *   3. only if the refresh fails do we dispatch `auth:unauthorized` (logout).
 * Each request is retried at most once (`_retry` guard), so a genuinely dead
 * session cannot loop.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Silently propagate intentional request cancellations (e.g., AbortController)
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthLifecycleEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;
      const result = await refreshSession();
      if (result.ok) {
        // Fresh access cookie in place — replay the original request. The
        // request interceptor re-runs, picking up the rotated CSRF cookie.
        return axiosInstance(originalRequest);
      }
      // Refresh failed — fall through to the logout handling below.
    }

    if (error.response?.status === 401) {
      const errorData = error.response.data as { error_description?: string } | undefined;
      const correlationId = error.response.headers['x-correlation-id'];

      const description = errorData?.error_description ?? 'Unauthorized';
      console.error(
        `Authentication error: ${description} (correlation_id: ${correlationId ?? 'N/A'})`
      );

      // Store the current URL for post-login redirect.
      // Skip during an in-flight logout — otherwise a late 401 from a pending
      // request can repopulate the key after clearAuthState() wiped it.
      const isLoggingOut = (window as unknown as { __heliosLoggingOut?: boolean })
        .__heliosLoggingOut;
      const currentPath = window.location.pathname + window.location.search;
      if (!isLoggingOut && currentPath !== '/login' && currentPath !== '/auth/callback') {
        localStorage.setItem('helios-post-login-redirect', currentPath);
      }

      // Redirect to login page (AuthContext will handle state clearing)
      // We dispatch a custom event that AuthContext will listen to
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    } else if (error.response) {
      // Log other errors with correlation ID
      const correlationId = error.response.headers['x-correlation-id'];
      console.error(
        `API error: ${error.response.status} - ${error.response.statusText} (correlation_id: ${correlationId ?? 'N/A'})`
      );
    } else if (error.request) {
      console.error('Network error: No response received from server');
    } else {
      console.error('Request setup error:', error.message);
    }

    // Announce the rejection so cross-cutting recovery can live in the domain that
    // understands it rather than here — the same separation as the `auth:unauthorized`
    // dispatch above. Last thing before the caller sees it, and only for failures
    // actually being surfaced: cancellations returned at the top, and a 401 that was
    // transparently replayed already returned too. Synchronous, so a listener may
    // rewrite `error.message` into something user-facing. See httpRejectionEvent.ts.
    notifyHttpRejection(error);

    return Promise.reject(error);
  }
);

export default axiosInstance;
